import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import supabase from '../../config/supabaseClient';
import { AuthContext } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { paymentMethodService } from '../../services/paymentMethodService';
import { tagService } from '../../services/tagService';

const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
`;

const Logo = styled(Link)`
  font-size: 24px;
  font-weight: bold;
  color: var(--text-primary);
  text-decoration: none;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  text-decoration: none;
  font-weight: ${props => props.active ? '500' : 'normal'};

  &:hover {
    color: black;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const NotificationBadge = styled.div`
  position: relative;
  cursor: pointer;
`;

const Badge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: black;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
`;

const ProfileButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: black;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
`;

const SubscriptionCount = styled.span`
  background: var(--count-badge);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 14px;
`;

const NotificationsDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: -16px;
  width: 320px;
  background: var(--surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--card-shadow);
  margin-top: 8px;
  z-index: 1000;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 24px;
    width: 12px;
    height: 12px;
    background: var(--surface);
    transform: rotate(45deg);
    border-left: 1px solid var(--border);
    border-top: 1px solid var(--border);
  }
`;

const NotificationHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  font-weight: 500;
  color: var(--text-primary);
`;

const NotificationList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  
  &:hover {
    background: var(--notification-hover);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  width: 32px;
  height: 32px;
  background: var(--count-badge);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const EmptyNotification = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--text-secondary);
`;

const ProfileDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 240px;
  background: var(--surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--card-shadow);
  margin-top: 8px;
  z-index: 1000;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 12px;
    width: 12px;
    height: 12px;
    background: var(--surface);
    transform: rotate(45deg);
    border-left: 1px solid var(--border);
    border-top: 1px solid var(--border);
  }
`;

const ProfileHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProfileAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--count-badge);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileName = styled.div`
  font-weight: 500;
`;

const ProfileMenu = styled.div`
  padding: 8px 0;
`;

const ProfileMenuItem = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: ${props => props.danger ? '#dc3545' : '#333'};

  &:hover {
    background: #f8f9fa;
  }
`;

const ThemeToggleGroup = styled.div`
  display: flex;
  padding: 8px 16px;
  gap: 16px;
  border-bottom: 1px solid var(--border);
`;

const ThemeToggle = styled.div`
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  background: ${props => props.active ? 'var(--hover)' : 'transparent'};
  
  &:hover {
    background: var(--hover);
  }
`;

const LogoutButton = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: var(--error);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const Count = styled.span`
  background: var(--primary-light);
  color: var(--primary);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
`;

const Header = () => {
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const { user, setUser } = useContext(AuthContext);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [paymentMethodCount, setPaymentMethodCount] = useState(0);
  const [tagCount, setTagCount] = useState(0);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadCounts = async () => {
      if (!user) return;

      try {
        const [subCount, pmCount, tCount] = await Promise.all([
          subscriptionService.getSubscriptionCount(user.id),
          paymentMethodService.getPaymentMethodCount(user.id),
          tagService.getTagCount(user.id)
        ]);

        setSubscriptionCount(subCount);
        setPaymentMethodCount(pmCount);
        setTagCount(tCount);
      } catch (error) {
        console.error('Error loading counts:', error);
      }
    };

    loadCounts();
  }, [user, location.pathname]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setShowProfile(false);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <HeaderContainer>
      <Logo to="/">Prenewal.</Logo>
      
      <Nav>
        <NavItem to="/dashboard" active={location.pathname === '/dashboard'}>
          <span>🏠</span>
          Dashboard
        </NavItem>
        
        <NavItem to="/subscriptions" active={location.pathname === '/subscriptions'}>
          <span>📦</span>
          Subscriptions
          <Count>{subscriptionCount}</Count>
        </NavItem>
        
        <NavItem to="/payment-methods" active={location.pathname === '/payment-methods'}>
          <span>💳</span>
          Payment Methods
          <Count>{paymentMethodCount}</Count>
        </NavItem>
        
        <NavItem to="/tags" active={location.pathname === '/tags'}>
          <span>🏷️</span>
          Tags
          <Count>{tagCount}</Count>
        </NavItem>
      </Nav>

     
    </HeaderContainer>
  );
};

export default Header; 